import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiKeyListOrderByEnum } from "@nens/rana/api-v1";
import { getRanaApiV1 } from "@nens/rana/api-v1";

import { createItemUpdaterForQueryLists } from "./hooks/create-item-updater-for-query-list";
import { QueryListReturn } from "./types/query-return";

const getShareKeyQuery = (apiKeyId?: string) => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["publication", "share_key", "stat", apiKeyId],
    queryFn: async () => {
      if (!apiKeyId) throw new Error("Never happens");
      const params = { apiKeyId };
      const response = await getRanaApiV1().ApiKeysApi.apiKeyGet(params);
      return response;
    },
    enabled: !!apiKeyId,
  });
};

export const useShareKeyQuery = (apiKeyId?: string) => {
  return useQuery(getShareKeyQuery(apiKeyId));
};

// LIST
interface PublicationApiKeyListParams {
  publicationId: string;
  limit?: number;
  offset?: number;
  orderBy?: ApiKeyListOrderByEnum;
}

const getShareKeyListQuery = (params: PublicationApiKeyListParams) => {
  const { publicationId, ...otherParams } = params;
  return getRanaApiV1().getQueryOptions({
    queryKey: ["publication", "share_key", "list", publicationId, otherParams],
    queryFn: async () => {
      const response = await getRanaApiV1().ApiKeysApi.apiKeyList({
        relatedToType: "publication",
        relatedToId: publicationId,
        ...otherParams,
      });
      return response;
    },
  });
};

export const useShareKeyListQuery = (request: PublicationApiKeyListParams) => {
  return useQuery(getShareKeyListQuery(request));
};

// INVALIDATION

export const useInvalidateShareKeyListQuery = () => {
  const queryClient = useQueryClient();
  return async (publicationId: string) => {
    await queryClient.invalidateQueries({
      queryKey: getShareKeyListQuery({ publicationId }).queryKey.slice(0, -1),
      refetchType: "all",
    });
  };
};

export const useUpdateShareKeyListsQuery = (publicationId: string) => {
  const queryClient = useQueryClient();
  return createItemUpdaterForQueryLists<QueryListReturn<typeof getShareKeyListQuery>>(queryClient, {
    getQueryKey: () => getShareKeyListQuery({ publicationId }).queryKey.slice(0, -1),
  });
};
